import classNames from "classnames";
import moment from "moment";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getBonusTypes,
  getClientsSuggestions,
  getCollaboratorNFHoursData,
  getCollaboratorsByRole,
  saveBonusTypes,
} from "../../../api";
import _ from "../../../i18n";
import { parsePreference } from "../../../utils";
import {
  Filters,
  FoldersQualityCard,
  Header,
  ProcessingDeadlineCard,
  ProcessingTimeCard,
  RatiosCard,
  Sidebar,
} from "./components";
import styles from "./index.module.scss";

const SIDEBAR_CHECK_LIST = [
  {
    key: "nf5_1",
    title: "Formation : Nettoyage hebdomadaire",
    description: "(NF5) : 3 heures par semaine, soit 12 heures par mois.",
    isCompleted: true,
    isOpened: false,
    weeklyValue: 0,
    monthlyValue: 0,
  },
  {
    key: "nf5_2",
    title: "Formation : Bilan mensuel",
    description: "(NF5) : 4 heures par mois.",
    isCompleted: false,
    isOpened: false,
    weeklyValue: 0,
    monthlyValue: 0,
  },
  {
    key: "nf6",
    title: "Réunions hebdomadaires",
    description: "(NF6) : 0,75 heure par semaine, soit 3 heures par mois.",
    isCompleted: false,
    isOpened: false,
    weeklyValue: 0,
    monthlyValue: 0,
  },
  {
    key: "nf4",
    title: "Besoin d'assistance",
    description:
      "(NF4) : 1,5 heure par semaine pour les juniors, soit 6 heures par mois.",
    isCompleted: true,
    isOpened: false,
    weeklyValue: 0,
    monthlyValue: 0,
  },
  {
    key: "ma5",
    title: "Production et suivi de production",
    description:
      "(MA5) : 1 heure par semaine pour le TP, 4 heures par semaine pour la production, soit 5 heures par mois en total.",
    isCompleted: false,
    isOpened: false,
    weeklyValue: 0,
    monthlyValue: 0,
  },
];

/*
 * RDT: Respect des délais de traitement
 * RTT: Respect du temps de traitement
 * RAT: Ratios
 * QD: Qualité des dossiers
 */

export default function BonusPage({
  filter,
  userSpace,
  collaborator,
  year,
  preferences,
}) {
  const allowViewDataBy =
    parsePreference(preferences).individualDevMa.allowViewDataBy;
  const { token, navCommunity } = useSelector((state) => state.auth);
  const [bonusTypes, setBonusTypes] = useState([]);
  const [isSavingBonusTypes, setIsSavingBonusTypes] = useState(false);
  const [isBonusTypesModalOpen, setIsBonusTypesModalOpen] = useState(false);
  const [bonusSectionsData, setBonusSectionsData] = useState({});
  const [collaboratorNFHoursData, setCollaboratorNFHoursData] = useState([]);
  const [sidebarChecksList, setSidebarChecksList] =
    useState(SIDEBAR_CHECK_LIST);
  const [isSidebarVisible, setDisplaySidebar] = useState(false);
  const [eligible, setEligible] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState({
    id: "ANNUAL",
    label: "annual",
    start: "01-01",
    end: "12-31",
  });
  const [filters, setFilters] = useState({
    ...filter,
    filterTab: [
      { id: "ANNUAL", label: "annual", start: "01-01", end: "12-31" },
      {
        id: "QUARTERLY",
        label: "quarterly",
        subTab: [
          {
            id: 1,
            label: "1er trimestre (du 01/01 au 31/03)",
            start: "01-01",
            end: "03-31",
            disabled: false,
          },
          {
            id: 2,
            label: "2ème trimestre (du 01/04 au 30/06)",
            start: "04-01",
            end: "06-30",
            disabled: moment().quarter() < 2 && moment().year() == year,
          },
          {
            id: 3,
            label: "3ème trimestre (du 01/07 au 30/09)",
            start: "07-01",
            end: "09-30",
            disabled: moment().quarter() < 3 && moment().year() == year,
          },
          {
            id: 4,
            label: "4ème trimestre (du 01/10 au 31/12)",
            start: "10-01",
            end: "12-31",
            disabled: moment().quarter() < 4 && moment().year() == year,
          },
        ],
      },
    ],
    period: { id: "ANNUAL", label: "annual", start: "01-01", end: "12-31" },
    subPeriod: null,
    countFilter: 0,
    encodingManagers: [],
    geOptions: [],
    encodingManager: userSpace ? userSpace : null,
    clientsOptions: [],
  });
  const [bonusTypesAmount, setBonusTypesAmount] = useState({
    rdt: 0,
    rtt: 0,
    rat: 0,
    qd: 0,
  });
  const [isLoadingTypesData, setIsLoadingTypesData] = useState({
    rdt: false,
    rtt: false,
    rat: false,
    qd: false,
  });

  const extractIndividualBonusType = (data) => {
    const rdtType = data.find((it) => it.abbr === "RDT");
    const rttType = data.find((it) => it.abbr === "RTT");
    const ratType = data.find((it) => it.abbr === "RAT");
    const qdType = data.find((it) => it.abbr === "QD");

    setBonusTypes(data);

    setBonusSectionsData({
      rdt: !rdtType ? null : { ...rdtType, isAchieved: false, percentage: 0 },
      rtt: !rttType ? null : { ...rttType, isAchieved: false, percentage: 0 },
      rat: !ratType ? null : { ...ratType, isAchieved: false, percentage: 0 },
      qd: !qdType ? null : { ...qdType, isAchieved: false, percentage: 0 },
    });
  };

  const getBonusTypesQuery = useQuery(["getBonusTypes", token], async () => {
    if (token) {
      try {
        const response = await getBonusTypes({ token });
        const data = response.data.data;

        extractIndividualBonusType(data);
      } catch (e) {
        return null;
      }
    }
  });

  let geQuery = useQuery(["getGeByRole", token, navCommunity], async () => {
    if (token && navCommunity) {
      try {
        const response = await getCollaboratorsByRole({
          token,
          organizationId: navCommunity.id,
          role: "encodingManager",
        });
        let data = response.data.data;
        let list = data
          .filter((item) => item !== null)
          .map((t) => {
            return {
              value: t.id,
              label: t.name,
            };
          });

        setFilters((prevFilters) => ({
          ...prevFilters,
          encodingManagers: data,
          encodingManager: userSpace ? userSpace : list[0]?.value,
          geOptions: list,
        }));
      } catch (e) {
        return null;
      }
    }
  });

  const getClientsQuery = useQuery(
    ["getClients", token, navCommunity],
    async () => {
      if (token && navCommunity) {
        try {
          const response = await getClientsSuggestions(
            token,
            null,
            navCommunity.id,
            null,
            year
          );
          let data = response.data.data;
          let list = data
            .filter((item) => item !== null)
            .map((t) => {
              return {
                value: t.id,
                label: t.name,
              };
            });

          setFilters((prevFilters) => ({
            ...prevFilters,
            clientsOptions: list,
          }));
        } catch (e) {
          return null;
        }
      }
    }
  );

  const getCollaboratorNFHoursDataQuery = useQuery(
    [
      "getCollaboratorNFHoursData",
      token,
      navCommunity,
      filters.encodingManager,
      year,
    ],
    async () => {
      if (token && navCommunity && filters.encodingManager) {
        try {
          const response = await getCollaboratorNFHoursData({
            token,
            organizationId: navCommunity.id,
            collaboratorId: filters.encodingManager,
            period: year,
          });

          setCollaboratorNFHoursData(response.data.data);
        } catch (e) {
          return null;
        }
      }
    }
  );

  useEffect(() => {
    if (Object.keys(collaboratorNFHoursData).length === 0) {
      return;
    }
    setSidebarChecksList((prevState) => {
      const currentYear = new Date().getFullYear();
      let currentMonth = new Date().getMonth() + 1;
      if (currentYear != year) {
        currentMonth = 12;
      }
      const nf5_1 = prevState.find((item) => item.key === "nf5_1");
      const nf5_2 = prevState.find((item) => item.key === "nf5_2");
      const nf6 = prevState.find((item) => item.key === "nf6");
      const nf4 = prevState.find((item) => item.key === "nf4");
      const ma5 = prevState.find((item) => item.key === "ma5");

      nf5_1.monthlyValue = 0;
      nf5_2.monthlyValue = 0;
      nf6.monthlyValue = 0;
      nf4.monthlyValue = 0;
      ma5.monthlyValue = 0;

      let numberOfMonths = 0;
      for (let i = 1; i <= currentMonth; i++) {
        const monthData =
          collaboratorNFHoursData[`${year}/${i.toString().padStart(2, "0")}`];
        // TODO: fix this two values...
        nf5_1.monthlyValue += monthData.nf5;
        nf5_2.monthlyValue += monthData.nf5;
        nf4.monthlyValue += monthData.nf4;
        nf6.monthlyValue += monthData.nf6;
        ma5.monthlyValue += monthData.ma5;
        numberOfMonths++;
      }

      const nf5_2_val = nf5_1.monthlyValue - 12 * 60 * numberOfMonths;
      nf5_1.monthlyValue =
        (nf5_2_val > 0 ? nf5_1.monthlyValue - nf5_2_val : nf5_1.monthlyValue) /
        12;
      nf5_2.monthlyValue = nf5_2_val > 0 ? nf5_2_val : 0;
      nf6.monthlyValue /= numberOfMonths;
      nf4.monthlyValue /= numberOfMonths;
      ma5.monthlyValue /= numberOfMonths;

      nf5_1.weeklyValue = nf5_1.monthlyValue / 4;
      nf5_2.weeklyValue = nf5_2.monthlyValue / 4;
      nf6.weeklyValue = nf6.monthlyValue / 4;
      nf4.weeklyValue = nf4.monthlyValue / 4;
      ma5.weeklyValue = ma5.monthlyValue / 4;

      nf5_1.isCompleted = nf5_1.monthlyValue === 12 && nf5_1.weeklyValue === 3;
      nf5_2.isCompleted = nf5_2.monthlyValue === 4;
      nf6.isCompleted = nf6.monthlyValue === 3 && nf6.weeklyValue === 0.75;
      nf4.isCompleted = nf4.monthlyValue === 6 && nf4.weeklyValue === 1.5;
      ma5.isCompleted = ma5.monthlyValue === 5 && ma5.weeklyValue === 1.25;

      return [nf5_1, nf5_2, nf6, nf4, ma5];
    });
  }, [collaboratorNFHoursData]);

  useEffect(() => {
    const eligibleList = sidebarChecksList.filter((item) => item.isCompleted);
    setEligible(eligibleList.length);
  }, [sidebarChecksList]);

  useEffect(() => {
    setFilters((prevFilter) => {
      const updatedFilterTab = prevFilter.filterTab.map((tab) => {
        if (tab.id === "QUARTERLY") {
          return {
            ...tab,
            subTab: tab.subTab.map((subTab) => {
              const disabled =
                subTab.id > 1
                  ? moment().quarter() < subTab.id && moment().year() == year
                  : false;

              return { ...subTab, disabled };
            }),
          };
        }
        return tab;
      });

      return {
        ...prevFilter,
        period: { id: "ANNUAL", label: "annual", start: "01-01", end: "12-31" },
        filterTab: updatedFilterTab,
      };
    });
    setSelectedFilter(filters.filterTab.find((item) => item.id === "ANNUAL"));
  }, [year]);

  const isActiveCard = (cardBonusType) => {
    const selectedFilterPeriod = filters.period.id.toLowerCase();
    if (!cardBonusType) return false;
    if (selectedFilterPeriod === "annual" && cardBonusType.isAnnual)
      return true;
    if (selectedFilterPeriod === "quarterly" && cardBonusType.isQuarterly)
      return true;

    return false;
  };

  const handleSaveBonusTypes = async () => {
    try {
      setIsSavingBonusTypes(true);
      const response = await saveBonusTypes({
        token,
        items: bonusTypes,
      });

      if (response.status === 200) {
        const data = response.data.data;
        setBonusTypes(data);
        extractIndividualBonusType(data);
        setIsBonusTypesModalOpen(false);
        toast.success("Les types de bonus sont enregistrés avec succès.");
      } else {
        toast.error(
          "Une erreur est survenue lors de la sauvegarde. Essayez une autre fois."
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSavingBonusTypes(false);
    }
  };

  const {
    encodingManagers,
    geOptions,
    encodingManager,
    clientsOptions
  } = filters;

  const selectedCollaborator =
    encodingManager && encodingManagers && encodingManagers.length > 0
      ? encodingManagers.find((ge) => ge.id == encodingManager)
      : null;

  const handleAmountChange = (type, amount) => {
    if (!["rdt", "rtt", "rat", "qd"].includes(type)) return;

    setBonusTypesAmount((prevState) => ({
      ...prevState,
      [type]: amount,
    }));
  };

  const handleLoadingData = (type, loadingStatus) => {
    if (!["rdt", "rtt", "rat", "qd"].includes(type)) return;

    setIsLoadingTypesData((prevState) => ({
      ...prevState,
      [type]: loadingStatus,
    }));
  };

  return (
    <div>
      {selectedCollaborator ? (
        <div className={styles.container}>
          <div
            className={classNames({
              [styles.overlay]: isSidebarVisible,
            })}
          />

          {/* {!userSpace && (
            <GroupFilter
              extraFilters={[
                {
                  type: "SELECT",
                  options: {
                    isSimple: true,
                    isClearable: false,
                    isSearch: true,
                    placeholder: _("collaborator"),
                    options: geOptions,
                    values: encodingManager,
                    action: handleGeChange,
                  },
                },
              ]}
              filter={filters}
              nbResult={filters.nbResult}
              updateFilter={setFilters}
              allowViewDataBy={
                parsePreference(preferences).individualDevMa.allowViewDataBy
              }
              collaborator={collaborator}
              preferences={preferences}
              subPreference="individualDevMa"
            />
          )} */}

            <Filters
              year={year}
              filters={filters}
              options={geOptions}
              setFilters={setFilters}
              setSelectedFilter={setSelectedFilter}
              bonusTypes={bonusTypes}
              setBonusTypes={setBonusTypes}
              isSavingBonusTypes={isSavingBonusTypes}
              handleSaveBonusTypes={handleSaveBonusTypes}
              isBonusTypesModalOpen={isBonusTypesModalOpen}
              setIsBonusTypesModalOpen={setIsBonusTypesModalOpen}
            />

          <>
            <Header
              collaborator={selectedCollaborator}
              eligible={eligible}
              totalBonusAmount={
                bonusTypesAmount.qd +
                bonusTypesAmount.rat +
                bonusTypesAmount.rdt +
                bonusTypesAmount.rtt
              }
              setDisplaySidebar={setDisplaySidebar}
              isLoadingData={
                isLoadingTypesData.rdt ||
                isLoadingTypesData.rtt ||
                isLoadingTypesData.rat ||
                isLoadingTypesData.qd
              }
            />

            <section className={styles.grid}>
              {bonusSectionsData.rdt !== null && (
                <ProcessingDeadlineCard
                  collaboratorId={selectedCollaborator.id}
                  filters={filters}
                  selectedPeriod={selectedFilter}
                  year={filters.year}
                  typeData={bonusTypes[0]}
                  isActive={isActiveCard(bonusTypes[0])}
                  clientsOptions={clientsOptions}
                  onAmountChange={handleAmountChange}
                  onLoadingData={handleLoadingData}
                />
              )}

              {bonusSectionsData.rtt !== null && (
                <ProcessingTimeCard
                  collaboratorId={selectedCollaborator.id}
                  filters={filters}
                  selectedPeriod={selectedFilter}
                  year={filters.year}
                  typeData={bonusTypes[1]}
                  isActive={isActiveCard(bonusTypes[1])}
                  clientsOptions={clientsOptions}
                  onAmountChange={handleAmountChange}
                  onLoadingData={handleLoadingData}
                />
              )}

              {bonusSectionsData.rat !== null && (
                <RatiosCard
                  collaboratorId={selectedCollaborator.id}
                  filters={filters}
                  selectedPeriod={selectedFilter}
                  year={filters.year}
                  typeData={bonusTypes[2]}
                  isActive={isActiveCard(bonusTypes[2])}
                  onAmountChange={handleAmountChange}
                  onLoadingData={handleLoadingData}
                />
              )}

              {bonusSectionsData.qd !== null && (
                <FoldersQualityCard
                  collaboratorId={selectedCollaborator.id}
                  filters={filters}
                  selectedPeriod={selectedFilter}
                  year={filters.year}
                  typeData={bonusTypes[3]}
                  isActive={isActiveCard(bonusTypes[3])}
                  onAmountChange={handleAmountChange}
                  onLoadingData={handleLoadingData}
                />
              )}
            </section>

            {isSidebarVisible && getCollaboratorNFHoursDataQuery.isFetched && (
              <Sidebar
                setShowSidebar={setDisplaySidebar}
                list={sidebarChecksList}
                setList={setSidebarChecksList}
                eligible={eligible}
              />
            )}
          </>
        </div>
      ) : (
        <div className="no_data">
          <div className="title">{_("noDataFound")}</div>
          <div className="subTitle">{_("noCollaboratorSelected")}</div>
        </div>
      )}
    </div>
  );
}
